import { useMemo, useState } from 'react'
import { Inbox, MoreHorizontal, Pencil, Trash2 } from 'lucide-react'
import { useForm } from '@inertiajs/react'
import { Alert } from '@/components/ui/Alert'
import { DataGrid, type GridColumn } from '@/components/blocks/data-grid'
import { Badge } from '@/components/ui/Badge'
import {
  type Column,
  DataTable,
  DataTableToolbar,
  applyTableState,
  useTableState,
} from '@/components/blocks/data-table'
import { Button } from '@/components/ui/Button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardList,
  CardListRow,
  CardTitle,
} from '@/components/ui/Card'
import { ControlBar, ControlBarGroup } from '@/components/ui/ControlBar'
import { Checkbox } from '@/components/ui/Checkbox'
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog'
import { DropdownMenu, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/DropdownMenu'
import { EmptyState } from '@/components/ui/EmptyState'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Kbd } from '@/components/ui/Kbd'
import { Progress } from '@/components/ui/Progress'
import { Segmented } from '@/components/ui/Segmented'
import { Select } from '@/components/ui/Select'
import { Popover } from '@/components/ui/Popover'
import { Separator } from '@/components/ui/Separator'
import {
  Sheet,
  SheetBody,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/Sheet'
import { Skeleton } from '@/components/ui/Skeleton'
import { Switch } from '@/components/ui/Switch'
import { Tabs } from '@/components/ui/Tabs'
import { Textarea } from '@/components/ui/Textarea'
import { useToast } from '@/components/ui/Toast'
import { count, money } from '@/lib'
import { PageHeader } from '@/components/shared'
import { CheckboxField, SelectField, SwitchField } from '@/components/shared/form'

/* ---------------------------------------------------------------- scaffolding */

function Section({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <section className="mb-8">
      <h2 className="text-fg m-0 text-base font-semibold">{title}</h2>
      {description && <p className="text-fg-muted mt-0.5 mb-3 text-sm">{description}</p>}
      <div className={description ? '' : 'mt-3'}>{children}</div>
    </section>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="border-border flex flex-wrap items-center gap-3 border-b py-2.5 last:border-b-0">
      <span className="t-label w-32 shrink-0">{label}</span>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  )
}

/* -------------------------------------------------------------------- tokens */

/* Grouped by the job, because the job is the whole point: the middle of each
 * ramp is almost always the one you want, and an opacity variant of any of them
 * is a new colour nobody named. */
const COLOR_GROUPS: { title: string; note: string; swatches: [string, string][] }[] = [
  {
    title: 'Surface',
    note: 'bg-elev is a card on the page; faint is a header wash; subtle is a row hover; hover is an already-filled control.',
    swatches: [
      ['--bg', 'bg-bg'],
      ['--bg-elev', 'bg-bg-elev'],
      ['--bg-faint', 'bg-bg-faint'],
      ['--bg-subtle', 'bg-bg-subtle'],
      ['--bg-hover', 'bg-bg-hover'],
    ],
  },
  {
    title: 'Line',
    note: 'subtle is a hairline inside a surface; border is the outline of one; strong is hover and emphasis.',
    swatches: [
      ['--border-subtle', 'bg-border-subtle'],
      ['--border', 'bg-border'],
      ['--border-strong', 'bg-border-strong'],
    ],
  },
  {
    title: 'Text',
    note: 'fg-hover is the primary button under a cursor and nothing else.',
    swatches: [
      ['--fg', 'bg-fg'],
      ['--fg-hover', 'bg-fg-hover'],
      ['--fg-muted', 'bg-fg-muted'],
      ['--fg-subtle', 'bg-fg-subtle'],
    ],
  },
  {
    title: 'Status',
    note: 'Each pairs with a -surface for the fill behind it. accent is focus rings and links only — never a button fill.',
    swatches: [
      ['--accent', 'bg-accent'],
      ['--accent-surface', 'bg-accent-surface'],
      ['--ok', 'bg-ok'],
      ['--ok-surface', 'bg-ok-surface'],
      ['--warn', 'bg-warn'],
      ['--warn-surface', 'bg-warn-surface'],
      ['--err', 'bg-err'],
      ['--err-surface', 'bg-err-surface'],
      ['--scrim', 'bg-scrim'],
    ],
  },
]

/* Every size in the scale, so a new one cannot be introduced without showing up
 * here next to the ones it has to sit beside. */
const TYPE_SCALE = [
  ['text-2xs', '11px — chips, initials tiles, keycaps; never a sentence'],
  ['text-xs', '12px — timestamps, ids, counters, badges, labels'],
  ['text-sm', '13px — the secondary line under a primary one'],
  ['text-base', '15px — the reading size: table cells, controls, body'],
  ['text-lg', '17px'],
  ['text-xl', '22px — a figure in a stat card'],
  ['text-2xl', '28px — page title'],
] as const

const ELEVATION = [
  ['shadow-2xs', 'A card or table at rest.'],
  ['shadow-xs', 'The same card under the cursor.'],
  ['shadow-raise', 'A thumb or pill lifted off its track.'],
  ['shadow-pop', 'Dropdown, popover, select menu, toast.'],
  ['shadow-modal', 'Dialog and sheet.'],
] as const

const DENSITY = [
  ['h-8', '32px — a control inside a dense row: the pager, a table-cell button.'],
  ['h-9', '36px — the default. Buttons, inputs, selects, the search box.'],
  ['h-10', '40px — a table header, and a control that has to carry a page.'],
  ['h-(--height-row)', '46px — one row of a table or grid. The only row height.'],
] as const

/* --------------------------------------------------------------- table demo */

interface DemoRow {
  id: string
  name: string
  platform: string
  status: string
  builds: number
}

const DEMO_ROWS: DemoRow[] = [
  { id: '1', name: 'season-14-live', platform: 'iOS', status: 'done', builds: 41 },
  { id: '2', name: 'season-14-beta', platform: 'Android', status: 'building', builds: 12 },
  { id: '3', name: 'hotfix-crash-loop', platform: 'iOS', status: 'failed', builds: 3 },
  { id: '4', name: 'arena-rebalance', platform: 'Android', status: 'done', builds: 27 },
  { id: '5', name: 'shop-copy-pass', platform: 'iOS', status: 'draft', builds: 1 },
  { id: '6', name: 'matchmaking-v3', platform: 'Android', status: 'done', builds: 55 },
  { id: '7', name: 'emote-pack-9', platform: 'iOS', status: 'building', builds: 8 },
]

const GRID_COLUMNS: GridColumn<DemoRow>[] = [
  { key: 'id', header: 'ID', width: '70px', type: 'read', sortable: true },
  { key: 'name', header: 'Branch', width: 'minmax(180px,1.5fr)', type: 'text', sortable: true },
  {
    key: 'platform',
    header: 'Platform',
    width: '140px',
    type: 'select',
    sortable: true,
    options: [
      { value: 'iOS', label: 'iOS' },
      { value: 'Android', label: 'Android', chipClassName: 'bg-ok-surface text-ok' },
    ],
  },
  { key: 'builds', header: 'Builds', width: '100px', type: 'number', align: 'end', sortable: true },
]

const DEMO_COLUMNS: Column<DemoRow>[] = [
  { key: 'name', header: 'Branch', sortable: true, width: 'minmax(160px,1.4fr)' },
  { key: 'platform', header: 'Platform', sortable: true, width: '120px' },
  {
    key: 'status',
    header: 'Status',
    width: '120px',
    cell: (row) => (
      <Badge
        dot
        variant={
          row.status === 'done'
            ? 'success'
            : row.status === 'building'
              ? 'warning'
              : row.status === 'failed'
                ? 'danger'
                : 'neutral'
        }
      >
        {row.status}
      </Badge>
    ),
  },
  { key: 'builds', header: 'Builds', align: 'end', sortable: true, width: '90px' },
]

const DEMO_FILTERS = [
  {
    key: 'platform',
    label: 'All platforms',
    options: [
      { value: '', label: 'All platforms' },
      { value: 'iOS', label: 'iOS' },
      { value: 'Android', label: 'Android' },
    ],
  },
  {
    key: 'status',
    label: 'All statuses',
    options: [
      { value: '', label: 'All statuses' },
      { value: 'done', label: 'done' },
      { value: 'building', label: 'building' },
      { value: 'failed', label: 'failed' },
      { value: 'draft', label: 'draft' },
    ],
  },
]

type TableMode = 'data' | 'loading' | 'empty'

/* ---------------------------------------------------------------------- page */

export function UiGalleryScreen() {
  const { toast } = useToast()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [switchOn, setSwitchOn] = useState(true)
  const [checked, setChecked] = useState(true)
  const [segment, setSegment] = useState<'day' | 'week' | 'month'>('week')
  const [fruit, setFruit] = useState('apple')
  const [tableMode, setTableMode] = useState<TableMode>('data')
  const [tab, setTab] = useState<'one' | 'two' | 'three'>('one')
  const [gridRows, setGridRows] = useState(DEMO_ROWS)

  const { state, setState } = useTableState({ pageSize: 5 })
  const table = useMemo(
    () => applyTableState(tableMode === 'empty' ? [] : DEMO_ROWS, DEMO_COLUMNS, state),
    [state, tableMode],
  )

  const form = useForm({ notify: true, digest: 'weekly', agree: false })

  return (
    <>
      <PageHeader
        title="Components"
        description="Every token, primitive and block in @roundhouse/ui, with its variants and states. This is the catalogue of what ports into roundhouse-rails — if a decision is not shown here, it is not a decision the design system has made."
      />

      <Section
        title="Colors"
        description="Semantic tokens only — never a hex literal, and never an opacity variant standing in for a missing token. If a value is missing, add a token."
      >
        <div className="flex flex-col gap-4">
          {COLOR_GROUPS.map((group) => (
            <div key={group.title}>
              <p className="t-label m-0">{group.title}</p>
              <p className="text-fg-subtle mt-0.5 mb-2 text-xs">{group.note}</p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
                {group.swatches.map(([name, className]) => (
                  <div key={name} className="border-border bg-bg-elev rounded-md border p-2">
                    <div className={`border-border mb-1.5 h-9 rounded-sm border ${className}`} />
                    <p className="text-fg-muted m-0 text-xs">{name}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section
        title="Typography"
        description="Composable @utility classes, not plain classes. One typeface throughout: there is no mono face, and nothing is uppercased by CSS."
      >
        <div className="border-border bg-bg-elev rounded-lg border px-4 py-3">
          <p className="t-display">t-display — page title</p>
          <p className="t-body mt-2">t-body — default reading size at 15px</p>
          <p className="t-small mt-1">t-small — secondary and caption text</p>
          <p className="t-label mt-2">t-label — column headers, field labels, group captions</p>
          <p className="t-tag mt-2">t-tag — Live, Prod, a channel or platform chip</p>
        </div>
      </Section>

      <Section
        title="Type scale"
        description="Seven sizes, and no component may write a pixel literal outside this list. 15px is the reading size — a table cell is reading."
      >
        <div className="divide-border-subtle border-border bg-bg-elev divide-y rounded-lg border">
          {TYPE_SCALE.map(([className, label]) => (
            <div key={className} className="flex items-baseline gap-4 px-4 py-2">
              <span className={`${className} text-fg w-56 shrink-0`}>{className}</span>
              <span className="text-fg-muted text-xs">{label}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section
        title="Numbers"
        description="Quantities are sans with tabular-nums — font-variant-numeric is set globally, so columns line up without a second typeface. Group thousands through count() / money(), never a bare toLocaleString(), which follows the visitor's locale."
      >
        <div className="border-border bg-bg-elev max-w-sm rounded-lg border px-4 py-3">
          {[1_284_003, 91_825, 412, 6.14].map((value) => (
            <div
              key={value}
              className="border-border-subtle flex items-baseline justify-between gap-4 border-b py-1.5 last:border-b-0"
            >
              <span className="text-fg-subtle text-xs">{value < 10 ? 'money()' : 'count()'}</span>
              <span className="text-fg font-medium tabular-nums">
                {value < 10 ? money(value) : count(value)}
              </span>
            </div>
          ))}
        </div>
      </Section>

      <Section
        title="Motion"
        description="One curve and two durations. Feedback under the cursor is 100ms; something arriving or leaving is 150ms. Nothing animates layout — no width, height, padding or inset — only transform and opacity."
      >
        <div className="flex flex-wrap gap-3">
          <div className="group border-border bg-bg-elev hover:border-border-strong hover:bg-bg-subtle rounded-lg border px-4 py-3 transition-colors duration-100 ease-out">
            <span className="text-fg-muted text-sm">duration-100 · hover me</span>
          </div>
          <div className="group border-border bg-bg-elev rounded-lg border px-4 py-3">
            <span className="text-fg-muted inline-block text-sm transition-transform duration-150 ease-out group-hover:translate-x-2">
              duration-150 · hover me
            </span>
          </div>
        </div>
      </Section>

      <Section
        title="Elevation"
        description="Named for what the thing is, not for a blur radius. No component spells out a box-shadow of its own."
      >
        <div className="flex flex-wrap gap-3">
          {ELEVATION.map(([className, label]) => (
            <div
              key={className}
              className={`border-border bg-bg-elev w-56 rounded-xl border px-3 py-2.5 ${className}`}
            >
              <p className="text-fg m-0 text-sm font-medium">{className}</p>
              <p className="text-fg-subtle mt-0.5 mb-0 text-xs">{label}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section
        title="Density"
        description="Four heights, one step apart. A control that is not one of these is a control that will not line up with the one beside it."
      >
        <div className="border-border bg-bg-elev divide-border-subtle divide-y rounded-lg border">
          {DENSITY.map(([className, label]) => (
            <div key={className} className="flex items-center gap-4 px-4 py-2">
              <span
                className={`bg-bg-subtle border-border-strong grid w-24 shrink-0 place-items-center rounded-md border text-xs ${className}`}
              >
                {className.replace('h-(--height-row)', 'row')}
              </span>
              <span className="text-fg-muted text-xs">{label}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Radius">
        <div className="flex flex-wrap gap-3">
          {[
            ['rounded-sm', '4px — badges, checkboxes, menu items'],
            ['rounded-md', '6px — buttons, inputs'],
            ['rounded-lg', '12px — nested panels, popovers, dialogs'],
            ['rounded-xl', '16px — cards and tables, the outermost surface'],
          ].map(([className, label]) => (
            <div key={className} className="flex items-center gap-2">
              <div className={`border-border-strong bg-bg-subtle size-9 border ${className}`} />
              <span className="text-fg-muted text-sm">{label}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Buttons">
        <div className="border-border bg-bg-elev rounded-lg border px-4 py-1">
          <Row label="Variants">
            <Button>Default</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="destructive">Destructive</Button>
          </Row>
          <Row label="Sizes">
            <Button size="sm">Small</Button>
            <Button>Default</Button>
            <Button size="lg">Large</Button>
            <Button size="icon" aria-label="More">
              <MoreHorizontal />
            </Button>
          </Row>
          <Row label="With icon">
            <Button>
              <Pencil />
              Edit
            </Button>
            <Button variant="destructive">
              <Trash2 />
              Delete
            </Button>
          </Row>
          <Row label="Disabled">
            <Button disabled>Default</Button>
            <Button variant="outline" disabled>
              Outline
            </Button>
            <Button variant="ghost" disabled>
              Ghost
            </Button>
          </Row>
        </div>
      </Section>

      <Section
        title="Fields"
        description="Field is pure presentation — no form library. The react-hook-form binding lives in the app."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Label" htmlFor="demo-plain">
            <Input id="demo-plain" placeholder="Placeholder" />
          </Field>
          <Field label="With hint" hint="Shown until an error replaces it." htmlFor="demo-hint">
            <Input id="demo-hint" placeholder="you@roundhouse.gg" />
          </Field>
          <Field label="Required" required error="This field is required." htmlFor="demo-error">
            <Input id="demo-error" aria-invalid placeholder="Something went wrong" />
          </Field>
          <Field label="Disabled" htmlFor="demo-disabled">
            <Input id="demo-disabled" disabled placeholder="Not editable" />
          </Field>
          <Field
            label="Label extra"
            labelExtra={
              <a href="#reset" className="text-accent text-xs hover:underline">
                Forgot?
              </a>
            }
            htmlFor="demo-extra"
          >
            <Input id="demo-extra" type="password" placeholder="••••••••" />
          </Field>
          <Field label="Textarea" htmlFor="demo-textarea">
            <Textarea id="demo-textarea" rows={3} placeholder="Release notes…" />
          </Field>
        </div>
      </Section>

      <Section
        title="Badges"
        description='size="tag" is the small chip — a channel, a platform, an environment. Write the label in the case you want to see; nothing uppercases it for you.'
      >
        <div className="border-border bg-bg-elev rounded-lg border px-4 py-1">
          <Row label="Variants">
            <Badge>Neutral</Badge>
            <Badge variant="success" dot>
              Success
            </Badge>
            <Badge variant="warning" dot>
              Warning
            </Badge>
            <Badge variant="danger" dot>
              Danger
            </Badge>
            <Badge variant="outline">Outline</Badge>
          </Row>
          <Row label="Tag size">
            <Badge size="tag" variant="outline">
              Prod
            </Badge>
            <Badge size="tag" variant="outline">
              App Store
            </Badge>
            <Badge size="tag" variant="success">
              Live
            </Badge>
          </Row>
        </div>
      </Section>

      <Section title="Toggles">
        <div className="border-border bg-bg-elev rounded-lg border px-4 py-1">
          <Row label="Switch">
            <Switch checked={switchOn} onCheckedChange={setSwitchOn} aria-label="Demo switch" />
            <Switch checked={false} disabled aria-label="Disabled switch" />
          </Row>
          <Row label="Checkbox">
            <Checkbox checked={checked} onCheckedChange={setChecked} aria-label="Demo checkbox" />
            <Checkbox checked disabled aria-label="Disabled checkbox" />
          </Row>
          <Row label="Segmented">
            <Segmented
              aria-label="Range"
              options={[
                { value: 'day', label: 'Day' },
                { value: 'week', label: 'Week' },
                { value: 'month', label: 'Month' },
              ]}
              value={segment}
              onValueChange={setSegment}
            />
          </Row>
          <Row label="Select">
            <Select
              aria-label="Fruit"
              options={[
                { value: 'apple', label: 'Apple' },
                { value: 'pear', label: 'Pear' },
                { value: 'plum', label: 'Plum' },
              ]}
              value={fruit}
              onValueChange={setFruit}
              triggerClassName="w-44"
            />
          </Row>
          <Row label="Kbd">
            <Kbd>⌘&nbsp;K</Kbd>
            <Kbd>Esc</Kbd>
          </Row>
          <Row label="Separator">
            <div className="flex w-full items-center gap-3">
              <span className="text-fg-muted text-sm">before</span>
              <Separator orientation="vertical" className="h-4" />
              <span className="text-fg-muted text-sm">after</span>
            </div>
          </Row>
        </div>
      </Section>

      <Section
        title="Form adapters"
        description="Thin wrappers binding the primitives to Inertia's useForm. They live beside the pages, not in components/ui, so no form library reaches a component."
      >
        <div className="border-border bg-bg-elev flex max-w-md flex-col gap-3 rounded-lg border px-4 py-4">
          <SwitchField
            form={form}
            name="notify"
            label="Email notifications"
            description="Send a note when a build finishes."
          />
          <SelectField
            form={form}
            name="digest"
            label="Digest"
            options={[
              { value: 'daily', label: 'Daily' },
              { value: 'weekly', label: 'Weekly' },
              { value: 'never', label: 'Never' },
            ]}
          />
          <CheckboxField form={form} name="agree" label="I understand this changes nothing" />
        </div>
      </Section>

      <Section title="Cards">
        <div className="grid gap-4 sm:grid-cols-2">
          <Card>
            <CardHeader>
              <div>
                <CardTitle>Card title</CardTitle>
                <CardDescription>A short description under the title.</CardDescription>
              </div>
              <Badge variant="outline" size="tag">
                Prod
              </Badge>
            </CardHeader>
            <CardContent>
              <p className="t-small m-0">Body content sits here.</p>
            </CardContent>
            <CardFooter>
              <span className="text-fg-muted text-sm">Footer note</span>
              <Button size="sm" variant="outline">
                Action
              </Button>
            </CardFooter>
          </Card>

          <Card>
            <CardContent>
              <EmptyState
                icon={<Inbox />}
                title="No builds yet"
                description="Builds appear here once a release is created."
                action={
                  <Button size="sm" variant="outline">
                    New Release
                  </Button>
                }
              />
            </CardContent>
          </Card>
        </div>
      </Section>

      <Section
        title="Card lists"
        description="Card + CardList + CardListRow — the shape every feed, log and history panel takes. Using it is what keeps eleven panels on one rhythm."
      >
        <Card>
          <CardHeader>
            <div className="min-w-0">
              <CardTitle>Recent Builds</CardTitle>
              <CardDescription>Newest first.</CardDescription>
            </div>
          </CardHeader>
          <CardList>
            {DEMO_ROWS.slice(0, 4).map((row) => (
              <CardListRow key={row.id}>
                <span className="text-fg min-w-0 flex-1 truncate text-xs font-semibold">
                  {row.name}
                </span>
                <Badge variant="outline">{row.platform}</Badge>
                <span className="text-2xs text-fg-subtle shrink-0 tabular-nums">
                  {row.builds} builds
                </span>
              </CardListRow>
            ))}
          </CardList>
        </Card>
      </Section>

      <Section
        title="Control bar"
        description="Page-level controls above a card. A surface, not a control set — DataTableToolbar is the search-and-filter row, and the two stack, this one on top."
      >
        <ControlBar>
          <ControlBarGroup>
            <Segmented
              aria-label="Range"
              options={[
                { value: 'day', label: 'Day' },
                { value: 'week', label: 'Week' },
                { value: 'month', label: 'Month' },
              ]}
              value={segment}
              onValueChange={setSegment}
            />
            <Select
              aria-label="Platform"
              options={[
                { value: 'ios', label: 'iOS' },
                { value: 'android', label: 'Android' },
              ]}
              value="ios"
              onValueChange={() => {}}
              triggerClassName="min-w-40 bg-bg-elev"
            />
          </ControlBarGroup>
          <Button>New Release</Button>
        </ControlBar>
      </Section>

      <Section title="Overlays">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => setDialogOpen(true)}>
            Open Dialog
          </Button>

          <DropdownMenu trigger={<Button variant="outline">Open menu</Button>} align="start">
            <DropdownMenuItem>
              <Pencil />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem>
              <MoreHorizontal />
              Duplicate
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem destructive>
              <Trash2 />
              Delete
            </DropdownMenuItem>
          </DropdownMenu>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogHeader>
            <DialogTitle>Dialog title</DialogTitle>
            <DialogDescription>
              Backdrop, focus trap and Escape handling come from the primitive.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <Field label="A field" htmlFor="dialog-field">
              <Input id="dialog-field" placeholder="Type something" />
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => setDialogOpen(false)}>Confirm</Button>
          </DialogFooter>
        </Dialog>
      </Section>

      <Section title="Skeleton">
        <div className="border-border bg-bg-elev flex max-w-sm flex-col gap-2 rounded-lg border px-4 py-4">
          <Skeleton className="h-3 w-40" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      </Section>

      <Section
        title="Alerts"
        description="Full-width banners. They stay until state changes — a toast does not."
      >
        <div className="border-border flex flex-col gap-2 overflow-hidden rounded-lg border">
          <Alert dot variant="warning">
            Maintenance scheduled 03:00 – 05:00 UTC
          </Alert>
          <Alert dot variant="danger">
            Android build 1.0.8 failed checksum validation
          </Alert>
          <Alert dot variant="success">
            Config published to production
          </Alert>
          <Alert variant="info">23 config files in the current bundle</Alert>
        </div>
      </Section>

      <Section title="Progress">
        <div className="border-border bg-bg-elev flex max-w-sm flex-col gap-3 rounded-lg border px-6 py-5">
          <Progress value={12} aria-label="12 percent" />
          <Progress value={64} aria-label="64 percent" />
          <Progress value={100} aria-label="complete" />
        </div>
      </Section>

      <Section
        title="Toasts"
        description="Transient confirmation. Stacks, auto-dismisses, and can be dismissed by hand."
      >
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => toast('Copied ConfigAds checksum')}>
            Default
          </Button>
          <Button variant="outline" onClick={() => toast('Config published to staging', 'success')}>
            Success
          </Button>
          <Button variant="outline" onClick={() => toast('Refund failed — try again', 'danger')}>
            Danger
          </Button>
        </div>
      </Section>

      <Section
        title="Sheet"
        description="Side panel for editing one record without losing the list behind it."
      >
        <Button variant="outline" onClick={() => setSheetOpen(true)}>
          Open Sheet
        </Button>

        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetHeader>
            <SheetTitle>Edit card</SheetTitle>
            <SheetDescription>Storm Colossus · Epic</SheetDescription>
          </SheetHeader>
          <SheetBody className="flex flex-col gap-3">
            <Field label="Elixir cost" htmlFor="sheet-elixir">
              <Input id="sheet-elixir" defaultValue="5" />
            </Field>
            <Field label="Damage" htmlFor="sheet-damage">
              <Input id="sheet-damage" defaultValue="412" />
            </Field>
          </SheetBody>
          <SheetFooter>
            <Button variant="outline" onClick={() => setSheetOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => setSheetOpen(false)}>Stage Change</Button>
          </SheetFooter>
        </Sheet>
      </Section>

      <Section
        title="Tabs"
        description="Underline tabs switch the whole view. Segmented, above, switches a value inside one."
      >
        <Tabs
          aria-label="Demo tabs"
          options={[
            { value: 'one', label: 'Cards' },
            { value: 'two', label: 'Arenas' },
            { value: 'three', label: 'Economy' },
          ]}
          value={tab}
          onValueChange={setTab}
        />
      </Section>

      <Section title="Popover">
        <Popover trigger={<Button variant="outline">Open popover</Button>} className="w-64 p-3">
          <p className="m-0 text-base font-medium">Anchored panel</p>
          <p className="text-fg-muted mt-1 mb-0 text-sm">
            Free-form content, unlike a menu. Used by the grid&apos;s select cell.
          </p>
        </Popover>
      </Section>

      <Section
        title="DataGrid"
        description="Spreadsheet-style editing. Every cell is an input; arrow keys and Enter walk the column, Escape lets go. Number columns show their separators at rest and the raw value while you type. Row controls go through the same rowActions slot DataTable uses, and + New sits in the footer beside the count."
      >
        <DataGrid
          columns={GRID_COLUMNS}
          rows={gridRows}
          total={gridRows.length}
          state={{ ...state, page: 1, pageSize: 10 }}
          onStateChange={setState}
          rowKey={(row) => row.id}
          noun="branches"
          addLabel="New branch"
          onCellChange={(row, key, value) =>
            setGridRows((previous) =>
              previous.map((entry) =>
                entry.id === row.id
                  ? { ...entry, [key]: key === 'builds' ? Number(value) || 0 : value }
                  : entry,
              ),
            )
          }
          rowActions={(row) => (
            <DropdownMenu
              trigger={
                <Button variant="ghost" size="icon" aria-label={`Actions for ${row.name}`}>
                  <MoreHorizontal />
                </Button>
              }
            >
              <DropdownMenuItem onClick={() => toast(`Opened ${row.name}`)}>
                <Pencil />
                Open Row
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                destructive
                onClick={() =>
                  setGridRows((previous) => previous.filter((entry) => entry.id !== row.id))
                }
              >
                <Trash2 />
                Delete Row
              </DropdownMenuItem>
            </DropdownMenu>
          )}
          onAddRow={() =>
            setGridRows((previous) => [
              {
                id: String(Date.now()),
                name: 'new-branch',
                platform: 'iOS',
                status: 'draft',
                builds: 0,
              },
              ...previous,
            ])
          }
        />
      </Section>

      <Section
        title="DataTable"
        description="Search, filter, sort and pagination. Toggle the state to review loading and empty without touching fixtures."
      >
        <Segmented
          aria-label="Table state"
          className="mb-3"
          options={[
            { value: 'data', label: 'Data' },
            { value: 'loading', label: 'Loading' },
            { value: 'empty', label: 'Empty' },
          ]}
          value={tableMode}
          onValueChange={setTableMode}
        />

        <DataTableToolbar
          state={state}
          onStateChange={setState}
          searchPlaceholder="Search branches…"
          filters={DEMO_FILTERS}
          className="mb-3"
        />

        <DataTable
          columns={DEMO_COLUMNS}
          rows={table.rows}
          total={table.total}
          state={state}
          onStateChange={setState}
          rowKey={(row) => row.id}
          loading={tableMode === 'loading'}
          minWidth={720}
          rowActions={(row) => (
            <DropdownMenu
              trigger={
                <Button variant="ghost" size="icon" aria-label={`Actions for ${row.name}`}>
                  <MoreHorizontal />
                </Button>
              }
            >
              <DropdownMenuItem>
                <Pencil />
                Edit Branch
              </DropdownMenuItem>
              <DropdownMenuItem destructive>
                <Trash2 />
                Delete
              </DropdownMenuItem>
            </DropdownMenu>
          )}
        />
      </Section>
    </>
  )
}
